import { forwardRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import ChevronsRightIcon from "@/app/shared-components/icons/ChevronsRightIcon";
import "swiper/css";

const slides = [
  {
    desktopImage: "/assets/images/win/1-botb-khaja-desktop.webp",
    mobileImage: "/assets/images/win/1-botb-khaja-mobile.webp",
    caption: "Khaja, Birmingham, £1.3M Dream Home Winner",
  },
  {
    desktopImage: "/assets/images/win/3-botb-souroush-desktop.webp",
    mobileImage: "/assets/images/win/3-botb-souroush-mobile.webp",
    caption: "Soroush, Leeds, £144 K Supercar Winner",
  },
  {
    desktopImage: "/assets/images/win/4-botb-ryan-desktop.webp",
    mobileImage: "/assets/images/win/4-botb-ryan-mobile.webp",
    caption: "Ryan, Jersey, £1.15 Million Dream House Winner",
  },
  {
    desktopImage: "/assets/images/win/2-botb-marius-desktop.webp",
    mobileImage: "/assets/images/win/2-botb-marius-mobile.webp",
    caption: "Marius, Tamworth, £500K Tax-Free Cash Winner",
  },
];

const asSeenOnLogos = [
  { src: "/assets/images/house-competition/logo_thesun.svg", alt: "logo The Sun" },
  { src: "/assets/images/house-competition/logo_mirror.svg", alt: "logo The Mirror" },
  { src: "/assets/images/house-competition/logo_dailystar.svg", alt: "logo Masthead Dailystar" },
  { src: "/assets/images/house-competition/logo_express.svg", alt: "logo Express" },
  { src: "/assets/images/house-competition/logo_dailymail.svg", alt: "logo Daily Mail" },
  { src: "/assets/images/house-competition/logo_4.svg", alt: "logo All4" },
  { src: "/assets/images/house-competition/logo_sky.svg", alt: "logo Sky" },
];

interface HeroSliderProps {
  offerData: {
    offerTitle: string;
    bonus: string;
    description: string;
  } | null;
  onGetBotbPass: () => void;
  onBuyTickets: () => void;
}

const HeroSlider = forwardRef<HTMLDivElement, HeroSliderProps>(({ offerData, onGetBotbPass, onBuyTickets }, ref) => {
  const urgencyText = offerData?.offerTitle || "FEW DAYS LEFT";
  const bonusText = offerData?.bonus || "New subscribers get";
  const descriptionRaw = offerData?.description || "20 EXTRA Devon Home Tickets*";
  const descriptionText = descriptionRaw.replace("*", "");
  const renderDescription = () => {
    const idx = descriptionText.toLowerCase().indexOf("extra");
    if (idx === -1) return descriptionText;
    const splitPos = idx + "extra".length;
    return (
      <>
        {descriptionText.slice(0, splitPos)}
        <br />
        {descriptionText.slice(splitPos)}
      </>
    );
  };

  return (
    <div ref={ref} className="relative w-full lg:mb-[29px]">
      <div className="relative w-full">
        {/* ─── Swiper ──────────────────────────────────────────────────────── */}
        <Swiper
          className="w-full"
          modules={[Autoplay]}
          loop={true}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          spaceBetween={0}
          slidesPerView={1}>
          {slides.map((slide, index) => (
            <SwiperSlide key={index}>
              {/* ── Mobile slide (image + winner text + AS SEEN ON) ─────────── */}
              <div className="lg:hidden relative h-[324px] overflow-hidden pt-5 px-3 pb-3">
                <img
                  src={slide.mobileImage}
                  alt={slide.caption}
                  className="absolute inset-0 object-cover size-full"
                  loading={index === 0 ? "eager" : "lazy"}
                />
                <div className="relative z-10 h-full flex flex-col justify-between">
                  {/* Winner container */}
                  <div className="flex flex-col gap-4 pl-1.5">
                    <div className="relative flex flex-col items-start gap-2">
                      <img
                        src="/assets/images/win/another-winner-text.webp"
                        alt="Another winner."
                        className="h-[39px] w-auto min-[380px]:max-[460px]:h-[36px]"
                      />
                      <img src="/assets/images/win/now-text.webp" alt="Now." className="h-[33px] w-auto" />
                    </div>
                    <div className="font-jost font-semibold text-[11px] leading-[14px] text-white [text-shadow:0.1px_0.1px_0px_black] w-[142px]">
                      {slide.caption}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Desktop slide ────────────────────────────────────────────── */}
              <div className="hidden lg:block relative h-[511px] overflow-hidden pb-[17px]">
                <img
                  src={slide.desktopImage}
                  alt={slide.caption}
                  className="absolute inset-0 object-cover size-full"
                  loading={index === 0 ? "eager" : "lazy"}
                />
                <div className="relative z-10 h-full w-full max-w-[1276px] mx-auto px-4 flex items-start pt-8 justify-end">
                  <div className="w-[365px] flex flex-col gap-[27px] mr-[15%]">
                    <div className="relative h-[135px]">
                      <img
                        src="/assets/images/win/another-winner-text.webp"
                        alt="Another winner."
                        className="absolute top-0 left-[5px] h-[65px] w-auto"
                      />
                      <img
                        src="/assets/images/win/now-text.webp"
                        alt="Now."
                        className="absolute top-[80px] left-0 h-[55px] w-auto"
                      />
                    </div>
                    <div className="font-jost font-medium text-[18px] leading-[23px] text-black/60 max-w-[234px]">
                      {slide.caption}
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="block lg:hidden absolute inset-0 z-10 pointer-events-none flex flex-col justify-end pt-5 px-3 pb-3">
          {/* AS SEEN ON */}
          <div className="flex flex-col gap-2">
            <div className="font-jost font-medium text-[8px] leading-3 text-black/60">AS SEEN ON</div>
            <div className="flex items-center gap-3">
              {asSeenOnLogos.map((logo, logoIndex) => (
                <img key={logoIndex} src={logo.src} alt={logo.alt} className="h-4 w-auto flex-1 opacity-60" />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Desktop: static left panel overlay ────────────────────────── */}
      <div className="hidden lg:flex absolute inset-0 z-10 pointer-events-none h-[511px] bg-[linear-gradient(to_right,white_60%,transparent_100%)] bg-[length:50%_100%] bg-no-repeat">
        <div className="w-full max-w-[1276px] mx-auto px-4 flex flex-col justify-between h-full">
          <div className="w-[553px] shrink-0 flex flex-col gap-3 pt-12 items-start">
            <div
              className="self-start h-[29px] flex items-center justify-center px-4 rounded-2xl"
              style={{ background: "linear-gradient(105deg, #ffeb60 1.6%, #c1b142 134.06%)" }}>
              <span className="font-jost font-semibold text-[16px] leading-[22px] text-black whitespace-nowrap">
                {urgencyText}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <div className="font-jost font-normal text-[22px] leading-7 text-secondary">{bonusText}</div>
              <div className="font-jost font-bold text-[48px] leading-[56px] text-dark-1 text-left">
                {renderDescription()}
                <span className="text-gray-5 font-normal">*</span>
              </div>
            </div>
            <div className="flex flex-col gap-3.5 mt-5 items-center pointer-events-auto">
              <button
                onClick={onGetBotbPass}
                className="bg-primary h-[72px] w-[388px] rounded-[7px] flex items-center justify-center">
                <span className="flex flex-col items-center">
                  <span className="flex items-center gap-2.5">
                    <span className="font-jost font-semibold text-[24px] leading-[30px] text-white">GET BOTB PASS</span>
                    <ChevronsRightIcon variant={4} className="size-[26px] text-white" />
                  </span>
                  <span className="font-jost font-normal text-[16px] leading-[22px] text-white/85">
                    from £9.99/month - cancel anytime
                  </span>
                </span>
              </button>
              <div className="font-jost font-normal text-[14px] leading-5 text-secondary">
                Or{" "}
                <button onClick={onBuyTickets} className="text-dark-1 underline decoration-solid">
                  buy individual tickets
                </button>{" "}
                from just 5p
              </div>
              <div className="font-jost font-normal text-[12px] leading-4 text-gray-5">
                {`18+. Enter at botb.com or free by post. T&Cs apply.`}
              </div>
            </div>
          </div>
          {/* Bottom: AS SEEN ON */}
          <div className="flex flex-col gap-4 pb-[17px]">
            <div className="font-jost font-medium text-[14px] leading-3 text-black/60">AS SEEN ON</div>
            <div className="flex items-center justify-between gap-3">
              {asSeenOnLogos.map((logo, logoIndex) => (
                <img key={logoIndex} src={logo.src} alt={logo.alt} className="h-8 w-auto opacity-60" />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Mobile: content-mobile (static, outside swiper) ────────────── */}
      <div className="lg:hidden flex flex-col gap-3 items-center px-4 pt-3.5 pb-[33px]">
        {/* Promo header */}
        <div className="flex flex-col gap-1 items-center w-full max-w-[280px]">
          <div
            className="h-4 flex items-center justify-center px-[10px] rounded-full"
            style={{ background: "linear-gradient(107deg, #ffeb60 1.6%, #c1b142 134.06%)" }}>
            <span className="font-jost font-bold italic text-[9px] leading-[10px] text-black whitespace-nowrap">
              {urgencyText}
            </span>
          </div>
          <div className="font-jost font-normal text-[14px] leading-5 text-secondary text-center">{bonusText}</div>
          <div className="font-jost font-bold text-[20px] leading-6 text-dark-1 text-center">
            {renderDescription()}
            <span className="text-gray-1 font-normal">*</span>
          </div>
        </div>
        {/* Actions */}
        <div className="flex flex-col gap-2 items-center w-full">
          <button onClick={onGetBotbPass} className="bg-primary h-12 w-64 rounded flex items-center justify-center">
            <span className="flex flex-col items-center">
              <span className="flex items-center gap-1.5">
                <span className="font-jost font-semibold text-[16px] leading-[22px] text-white">GET BOTB PASS</span>
                <ChevronsRightIcon variant={4} className="size-4 text-white" />
              </span>
              <span className="font-jost font-medium text-[11px] leading-[14px] text-white/80">
                from £9.99/month - cancel anytime
              </span>
            </span>
          </button>
          <div className="font-jost font-normal text-[11px] leading-[14px] text-secondary text-center">
            Or{" "}
            <button onClick={onBuyTickets} className="text-dark-1 underline decoration-solid">
              buy individual tickets
            </button>{" "}
            from just 5p
          </div>
        </div>
        <div className="font-jost font-normal text-[10px] leading-3 text-gray-5 text-center">
          {`18+. Enter at botb.com or free by post. T&Cs apply.`}
        </div>
      </div>
    </div>
  );
});

HeroSlider.displayName = "HeroSlider";

export default HeroSlider;
